<template>
    <article
        :id="`reply-${reply.id}`"
        class="discussion-reply rounded-xl text-foxy-text"
        :class="{
            'discussion-reply--root': !isDescendant,
            'discussion-reply--descendant': isDescendant,
            'discussion-reply--tombstone': reply.is_tombstone,
            'highlighted-reply': isHighlighted,
        }"
        :data-reply-id="reply.id"
        tabindex="-1"
    >
        <div class="px-3 py-3 sm:px-4">
            <div class="mb-2 flex min-w-0 items-start justify-between gap-2">
                <a
                    v-if="reply.author && reply.author.username"
                    :href="profileUrl(reply.author.username)"
                    class="group flex min-w-0 items-center gap-2 sm:gap-3"
                >
                    <user-avatar
                        :src="reply.author.avatar_display || ''"
                        :name="reply.author.name"
                        size="xs"
                        class="shrink-0 text-xs sm:!h-8 sm:!w-8 sm:!basis-8"
                    />
                    <span class="min-w-0">
                        <span class="flex min-w-0 flex-wrap items-baseline gap-x-2">
                            <span class="truncate text-sm font-medium transition-colors group-hover:text-foxy-action sm:text-base">
                                {{ reply.author.name }}
                            </span>
                            <span class="foxy-muted whitespace-nowrap text-xs sm:text-sm">{{ formatDate(reply.created_at) }}</span>
                        </span>
                        <span class="foxy-muted block truncate text-xs sm:text-sm">@{{ reply.author.username }}</span>
                    </span>
                </a>
                <div v-else class="flex min-w-0 items-center gap-2 sm:gap-3">
                    <user-avatar
                        :src="''"
                        :name="reply.author ? reply.author.name : 'Користувач, якого видалено'"
                        size="xs"
                        class="shrink-0 text-xs sm:!h-8 sm:!w-8 sm:!basis-8"
                    />
                    <span class="min-w-0">
                        <span class="block truncate text-sm font-medium sm:text-base">
                            {{ reply.author ? reply.author.name : 'Користувач, якого видалено' }}
                        </span>
                        <span class="foxy-muted whitespace-nowrap text-xs sm:text-sm">{{ formatDate(reply.created_at) }}</span>
                    </span>
                </div>
            </div>

            <div v-if="isDescendant && reply.target" class="mb-2 min-w-0 text-xs sm:text-sm">
                <button
                    v-if="reply.target.available"
                    type="button"
                    class="max-w-full cursor-pointer break-words text-left font-medium text-foxy-action transition-colors hover:text-foxy-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30"
                    @click="$emit('focus-reply', reply.target.id)"
                >
                    ↪ {{ reply.target.label }}
                </button>
                <span v-else class="foxy-muted block max-w-full break-words">↪ {{ reply.target.label }}</span>
            </div>

            <div v-if="reply.is_tombstone" class="rounded-lg border border-dashed border-foxy-border bg-foxy-elevated/45 px-3 py-3 text-sm italic text-foxy-text-secondary">
                Повідомлення видалено автором.
            </div>

            <template v-else>
                <div v-if="!isEditing">
                    <div v-if="reply.status === 'blocked'" class="my-3 rounded-lg border-2 border-red-300 bg-red-50/50 p-4 dark:border-red-700 dark:bg-red-900/20">
                        <div class="mb-3 overflow-hidden rounded-md">
                            <div class="break-words text-sm leading-relaxed blur-sm filter sm:text-base" v-html="formatContent(reply.content)"></div>
                        </div>
                        <p class="border-t border-red-200 pt-2 text-xs font-medium text-red-700 dark:border-red-800 dark:text-red-300">
                            Контент заблоковано адміністрацією сайту
                        </p>
                        <p v-if="reply.moderation_reason" class="mt-1 text-xs text-red-600 dark:text-red-400">
                            Причина: {{ reply.moderation_reason }}
                        </p>
                    </div>
                    <div
                        v-else
                        class="break-words text-sm leading-relaxed sm:text-base"
                        style="word-break: break-word; overflow-wrap: anywhere"
                        v-html="formatContent(reply.content)"
                    ></div>
                </div>

                <div v-else class="mb-3">
                    <textarea
                        v-model="editContent"
                        class="foxy-textarea !resize-none !border-0 !p-3 !text-sm ring-1 ring-inset ring-foxy-border sm:!p-4 sm:!text-base"
                        rows="3"
                        maxlength="1000"
                    ></textarea>
                    <div class="mt-3 flex justify-end gap-2">
                        <button type="button" class="foxy-btn-secondary !border-0 !px-3 !py-2 !text-sm" @click="cancelEdit">Скасувати</button>
                        <button type="button" class="foxy-btn-primary !px-3 !py-2 !text-sm" :disabled="isSaving" @click="saveEdit">
                            {{ isSaving ? 'Збереження…' : 'Зберегти' }}
                        </button>
                    </div>
                </div>

                <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div class="flex items-center gap-3">
                        <button
                            v-if="reply.capabilities.can_like"
                            type="button"
                            class="min-h-11 min-w-11 rounded-lg px-2 text-foxy-text-secondary transition-colors hover:text-red-500"
                            :aria-label="isLiked ? 'Прибрати вподобання' : 'Вподобати відповідь'"
                            @click="toggleLike"
                        >
                            <i :class="['fas fa-heart', isLiked ? 'text-red-500' : '']"></i>
                            <span class="ml-1 text-xs sm:text-sm">{{ likesCount }}</span>
                        </button>
                        <span v-else class="flex min-h-11 items-center px-2 text-foxy-text-secondary" aria-label="Кількість вподобань">
                            <i class="fas fa-heart"></i>
                            <span class="ml-1 text-xs sm:text-sm">{{ likesCount }}</span>
                        </span>
                    </div>

                    <div class="flex flex-wrap items-center justify-end gap-1">
                        <button
                            v-if="reply.capabilities.can_reply"
                            type="button"
                            class="min-h-11 rounded-lg px-3 text-sm text-foxy-text-secondary transition-colors hover:bg-foxy-elevated hover:text-foxy-action"
                            @click="$emit('reply-requested', reply)"
                        >
                            <i class="fas fa-reply mr-1 text-xs"></i>Відповісти
                        </button>
                        <button
                            v-if="reply.capabilities.can_edit"
                            type="button"
                            class="min-h-11 min-w-11 rounded-lg text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-action"
                            aria-label="Редагувати відповідь"
                            @click="startEdit"
                        >
                            <i class="fas fa-edit"></i>
                        </button>
                        <button
                            v-if="reply.capabilities.can_delete"
                            type="button"
                            class="min-h-11 min-w-11 rounded-lg text-foxy-error hover:bg-foxy-error/10"
                            :disabled="isDeleting"
                            aria-label="Видалити відповідь"
                            @click="deleteReply"
                        >
                            <i class="fas fa-trash"></i>
                        </button>
                        <report-button
                            v-if="reply.capabilities.can_report"
                            :reportable-type="'App\\Models\\DiscussionReply'"
                            :reportable-id="reply.id"
                            :content-preview="contentPreview"
                            :content-url="contentUrl"
                        ></report-button>
                    </div>
                </div>
            </template>
        </div>
    </article>
</template>

<script>
import axios from 'axios';

export default {
    name: 'DiscussionReply',
    props: {
        reply: { type: Object, required: true },
        isDescendant: { type: Boolean, default: false },
        discussionSlug: { type: String, required: true },
        highlightedReplyId: { type: Number, default: null },
    },
    data() {
        return {
            isEditing: false,
            editContent: '',
            isSaving: false,
            isDeleting: false,
            isLiked: Boolean(this.reply.viewer_liked),
            likesCount: Number(this.reply.likes_count || 0),
        };
    },
    computed: {
        isHighlighted() {
            return Number(this.highlightedReplyId) === Number(this.reply.id);
        },
        contentPreview() {
            const content = this.reply.content || '';
            return content.substring(0, 100) + (content.length > 100 ? '…' : '');
        },
        contentUrl() {
            return `/discussions/${this.discussionSlug}#reply-${this.reply.id}`;
        },
    },
    watch: {
        reply: {
            deep: true,
            handler(reply) {
                this.isLiked = Boolean(reply.viewer_liked);
                this.likesCount = Number(reply.likes_count || 0);
            },
        },
    },
    methods: {
        profileUrl(username) {
            return username ? `/users/${username}` : '#';
        },
        formatContent(content) {
            if (!content) return '';

            const escaped = content
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
            const mentionRegex = /@([a-zA-Zа-яА-ЯёЁіІїЇєЄ0-9_-]+)/gu;

            return escaped.replace(mentionRegex, (match, username) => (
                `<a href="/users/${username}" class="mention-link font-medium text-foxy-action transition-colors hover:text-foxy-brand">@${username}</a>`
            ));
        },
        formatDate(dateString) {
            if (!dateString) return 'щойно';
            const date = new Date(dateString);
            const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
            if (seconds < 60) return 'щойно';
            if (seconds < 3600) return `${Math.floor(seconds / 60)} хв тому`;
            if (seconds < 86400) return `${Math.floor(seconds / 3600)} год тому`;
            if (seconds < 604800) return `${Math.floor(seconds / 86400)} дн тому`;
            return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' });
        },
        async toggleLike() {
            if (!this.reply.capabilities.can_like) return;
            try {
                const { data } = await axios.post(`/discussions/${this.discussionSlug}/replies/${this.reply.id}/like`);
                if (data.success) {
                    this.isLiked = Boolean(data.liked);
                    this.likesCount = Number(data.likes_count || 0);
                    this.$emit('like-toggled', {
                        replyId: this.reply.id,
                        liked: this.isLiked,
                        likesCount: this.likesCount,
                    });
                }
            } catch (error) {
                this.$emit('show-notification', error.response?.data?.message || 'Не вдалося змінити вподобання.', 'error');
            }
        },
        startEdit() {
            this.editContent = this.reply.content || '';
            this.isEditing = true;
        },
        cancelEdit() {
            this.isEditing = false;
            this.editContent = '';
        },
        async saveEdit() {
            if (!this.editContent.trim() || this.isSaving) return;
            this.isSaving = true;
            try {
                const { data } = await axios.put(`/discussions/${this.discussionSlug}/replies/${this.reply.id}`, {
                    content: this.editContent,
                });
                if (data.success) {
                    this.isEditing = false;
                    this.$emit('threads-replaced', data.threads || []);
                    this.$emit('show-notification', 'Відповідь оновлено!', 'success');
                }
            } catch (error) {
                this.$emit('show-notification', error.response?.data?.message || 'Не вдалося оновити відповідь.', 'error');
            } finally {
                this.isSaving = false;
            }
        },
        async deleteReply() {
            if (this.isDeleting) return;
            this.isDeleting = true;
            try {
                const confirmed = await window.confirm(
                    'Ви впевнені, що хочете видалити цю відповідь?',
                    'Підтвердження',
                    'warning',
                );
                if (!confirmed) return;

                const { data } = await axios.delete(`/discussions/${this.discussionSlug}/replies/${this.reply.id}`);
                if (data.success) {
                    this.$emit('threads-replaced', data.threads || []);
                    this.$emit('show-notification', 'Відповідь видалено!', 'success');
                }
            } catch (error) {
                this.$emit('show-notification', error.response?.data?.message || 'Не вдалося видалити відповідь.', 'error');
            } finally {
                this.isDeleting = false;
            }
        },
    },
};
</script>

<style scoped>
.discussion-reply {
    margin-bottom: 0.75rem;
    transition: background-color 0.2s ease, outline-color 0.2s ease;
}

.discussion-reply--root {
    background-color: rgb(var(--foxy-elevated) / 0.42);
}

.discussion-reply--descendant {
    border-left: 1px solid rgb(var(--foxy-text-secondary) / 0.32);
    border-radius: 0;
    padding-left: 0.75rem;
}

.discussion-reply--tombstone {
    color: rgb(var(--foxy-text-secondary));
}

.highlighted-reply {
    background-color: rgb(var(--foxy-action) / 0.1);
    outline: 2px solid rgb(var(--foxy-action) / 0.55);
    outline-offset: 0.2rem;
}

@media (max-width: 390px) {
    .discussion-reply--descendant {
        padding-left: 0.75rem;
    }
}

@media (prefers-reduced-motion: reduce) {
    .discussion-reply {
        transition: none;
    }
}
</style>
